import { disablePush } from '../lib/push'
import { NavLink, useLocation, useNavigate } from 'react-router'
import { ChartLine, ClipboardCheck, Headset, House, Inbox, LogOut, Menu, MessagesSquare, Rocket, Search, Settings, ShieldCheck, SlidersHorizontal, Sparkles, Users, X, type LucideIcon } from 'lucide-react'
import type { Space } from '@takeit4me/shared'
import { useEffect, useState } from 'react'
import { useAuth, useMe } from '../auth'
import { initials } from '../lib/format'
import { ROLE_LABELS } from '../lib/labels'
import { MOCK } from '../api'
import { TenantLogo } from './ui'

export const SPACE_META: Record<Space, { label: string; icon: LucideIcon; path: string }> = {
  today: { label: 'Today', icon: House, path: '/today' },
  exceptions: { label: 'Exceptions', icon: Inbox, path: '/exceptions' },
  live: { label: 'Live', icon: Headset, path: '/live' },
  crm: { label: 'CRM', icon: Users, path: '/crm' },
  tasks: { label: 'Tasks', icon: ClipboardCheck, path: '/tasks' },
  studio: { label: 'Studio', icon: SlidersHorizontal, path: '/studio' },
  improve: { label: 'Improve', icon: Sparkles, path: '/improve' },
  analytics: { label: 'Analytics', icon: ChartLine, path: '/analytics' },
  settings: { label: 'Settings', icon: Settings, path: '/settings' },
}

/** On a phone the first spaces sit in a bottom tab bar (4-03); the rest scroll sideways in the same bar. */
export function Sidebar({ badges }: { badges: Partial<Record<Space, number>> }) {
  const me = useMe()
  const { signOut } = useAuth()
  const [menu, setMenu] = useState(false)
  const spaces = me.spaces.filter((s) => s !== 'settings')
  const item = (s: Space) => {
    const m = SPACE_META[s]
    const Icon = m.icon
    const b = badges[s]
    return (
      <NavLink key={s} to={m.path} title={m.label} aria-label={m.label}
        className={({ isActive }) => `group relative w-10 h-10 max-md:w-12 max-md:h-11 shrink-0 rounded-[10px] flex items-center justify-center transition-colors ${isActive ? 'bg-sideon text-white' : 'text-indigo-300 hover:bg-white/10 hover:text-white'}`}>
        <Icon size={20} strokeWidth={1.8} />
        {!!b && <span className="absolute top-1 right-0.5 bg-rose-500 text-white text-[9px] font-bold rounded-lg px-1 leading-[14px] min-w-[14px] text-center">{b > 99 ? '99+' : b}</span>}
        <span className="pointer-events-none absolute left-12 z-50 hidden md:group-hover:block whitespace-nowrap rounded-md bg-slate-900 text-white text-[11.5px] font-semibold px-2 py-1 shadow">{m.label}</span>
      </NavLink>
    )
  }
  return (
    <nav aria-label="Spaces" className="w-16 shrink-0 bg-side flex flex-col items-center py-3.5 gap-1.5 max-md:order-last max-md:w-full max-md:h-14 max-md:flex-row max-md:py-1 max-md:px-2 max-md:gap-1 max-md:overflow-x-auto max-md:pb-[env(safe-area-inset-bottom)]">
      <NavLink to="/today" className="w-9 h-9 rounded-[10px] text-white font-extrabold text-[13px] flex items-center justify-center mb-3.5 tracking-tight max-md:hidden" style={{ background: 'linear-gradient(135deg,#6366f1,#14b8a6)' }} aria-label="takeit4me home">t4</NavLink>
      {spaces.flatMap((s) => (s === 'exceptions' ? [item(s), <NavLink key="conversations" to="/conversations" title="Conversations" aria-label="Conversations" className={({ isActive }) => `group relative w-10 h-10 max-md:w-12 max-md:h-11 shrink-0 rounded-[10px] flex items-center justify-center transition-colors ${isActive ? 'bg-sideon text-white' : 'text-indigo-300 hover:bg-white/10 hover:text-white'}`}><MessagesSquare size={20} strokeWidth={1.8} /><span className="pointer-events-none absolute left-12 z-50 hidden md:group-hover:block whitespace-nowrap rounded-md bg-slate-900 text-white text-[11.5px] font-semibold px-2 py-1 shadow">Conversations</span></NavLink>] : [item(s)]))}
      <div className="flex-1 max-md:min-w-2" />
      {me.spaces.includes('settings') && item('settings')}
      <div className="relative mt-2 max-md:mt-0 shrink-0">
        <button onClick={() => setMenu((v) => !v)} className="w-8 h-8 rounded-full bg-indigo-200 text-sideon font-bold text-[12px] flex items-center justify-center" title={me.staff.name} aria-label="Your account">
          {initials(me.staff.name)}
        </button>
        {menu && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setMenu(false)} />
            <div className="absolute left-11 bottom-0 z-50 w-60 card shadow-xl p-3 max-md:fixed max-md:left-auto max-md:right-3 max-md:bottom-16">
              <div className="font-semibold">{me.staff.name}</div>
              <div className="text-muted text-[12px]">{me.staff.email}</div>
              <div className="text-muted text-[12px] mt-1">{ROLE_LABELS[me.staff.role]} · {me.company.name}</div>
              <NavLink to="/account" className="btn w-full mt-3" onClick={() => setMenu(false)}><ShieldCheck /> Sign-in and security</NavLink>
              {me.staff.role === 'admin' && me.lifecycle && me.lifecycle.status !== 'active' && <NavLink to="/onboarding" className="btn w-full mt-2" onClick={() => setMenu(false)}><Rocket /> Getting started</NavLink>}
              <div className="flex flex-col mt-2 text-[13px]">
                <NavLink className="py-1 font-semibold text-brand" to="/notifications" onClick={() => setMenu(false)}>Notifications and settings</NavLink>
                {me.staff.role !== 'consultant' && <NavLink className="py-1 font-semibold text-brand" to="/rota" onClick={() => setMenu(false)}>Hours, rota and my leave</NavLink>}
              </div>
              {MOCK ? (
                <div className="mt-3 text-[11.5px] text-muted">Demo mode: changes stay in this browser tab.</div>
              ) : (
                <button className="btn w-full mt-2" onClick={() => { setMenu(false); void disablePush().catch(() => {}).finally(() => signOut()) }}><LogOut /> Sign out</button>
              )}
            </div>
          </>
        )}
      </div>
    </nav>
  )
}

/**
 * The shell at phone width (design 12.2): a compact top bar with a menu button that opens a
 * slide-over drawer with the same spaces as the sidebar. The Exceptions shortcut stays one tap away.
 */
export function PhoneNav({ badges }: { badges: Partial<Record<Space, number>> }) {
  const me = useMe()
  const { signOut } = useAuth()
  const loc = useLocation()
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  useEffect(() => { setOpen(false) }, [loc.pathname])
  useEffect(() => {
    if (!open) return
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [open])
  const spaces = me.spaces.filter((s) => s !== 'settings')
  const current = (Object.keys(SPACE_META) as Space[]).find((s) => loc.pathname === SPACE_META[s].path || loc.pathname.startsWith(SPACE_META[s].path + '/'))
  const desk = badges.exceptions ?? 0
  const row = (s: Space) => {
    const m = SPACE_META[s]
    const Icon = m.icon
    const b = badges[s]
    return (
      <NavLink key={s} to={m.path}
        className={({ isActive }) => `flex items-center gap-3 h-12 px-3 rounded-[10px] font-semibold text-[14px] ${isActive ? 'bg-sideon text-white' : 'text-indigo-200 active:bg-white/10'}`}>
        <Icon size={20} strokeWidth={1.8} />
        <span className="flex-1">{m.label}</span>
        {!!b && <span className="bg-rose-500 text-white text-[11px] font-bold rounded-full px-2 leading-[20px] min-w-[20px] text-center">{b > 99 ? '99+' : b}</span>}
      </NavLink>
    )
  }
  return (
    <>
      <header className="h-12 shrink-0 bg-side text-white flex items-center gap-1 px-1.5 min-w-0">
        <button onClick={() => setOpen(true)} aria-label="Open menu" aria-expanded={open} className="relative w-11 h-11 rounded-lg flex items-center justify-center active:bg-white/10">
          <Menu size={22} />
        </button>
        <div className="flex-1 min-w-0 font-bold text-[15px] truncate">{current ? SPACE_META[current].label : 'takeit4me'}</div>
        {me.spaces.includes('exceptions') && current !== 'exceptions' && (
          <NavLink to="/exceptions" aria-label={`Exceptions${desk ? `, ${desk} waiting` : ''}`} className="relative w-11 h-11 rounded-lg flex items-center justify-center text-indigo-200 active:bg-white/10">
            <Inbox size={20} />
            {!!desk && <span className="absolute top-1.5 right-1 bg-rose-500 text-white text-[9px] font-bold rounded-lg px-1 leading-[14px] min-w-[14px] text-center">{desk > 99 ? '99+' : desk}</span>}
          </NavLink>
        )}
        <div className="pr-1.5"><TenantLogo letter={me.company.logo_letter} color={me.company.color} url={me.company.logo_url} size={26} /></div>
      </header>
      {open && (
        <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="absolute inset-0 bg-slate-900/45" onClick={() => setOpen(false)} />
          <nav className="t4-drawer relative h-full w-[290px] max-w-[85vw] bg-side text-white flex flex-col shadow-2xl">
            <div className="h-14 shrink-0 flex items-center gap-2.5 px-3">
              <div className="w-9 h-9 rounded-[10px] text-white font-extrabold text-[13px] flex items-center justify-center tracking-tight" style={{ background: 'linear-gradient(135deg,#6366f1,#14b8a6)' }}>t4</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[14px] truncate">{me.company.name}</div>
                {MOCK && <div className="text-[11px] text-amber-300 font-semibold">Demo</div>}
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close menu" className="w-11 h-11 rounded-lg flex items-center justify-center text-indigo-200 active:bg-white/10"><X size={20} /></button>
            </div>
            {me.spaces.includes('crm') && (
              <form className="mx-3 mb-2 h-11 rounded-lg bg-white/10 flex items-center px-3 gap-2 text-indigo-200 focus-within:ring-2 focus-within:ring-indigo-400"
                onSubmit={(e) => { e.preventDefault(); if (q.trim()) { nav(`/crm/people?q=${encodeURIComponent(q.trim())}`); setOpen(false) } }}>
                <Search size={16} className="shrink-0" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers" aria-label="Search customers" className="flex-1 min-w-0 bg-transparent outline-none text-white placeholder:text-indigo-300 text-[14px]" />
              </form>
            )}
            <div className="flex-1 overflow-y-auto px-2 flex flex-col gap-0.5">
              {spaces.flatMap((s) => (s === 'exceptions' ? [row(s), (
                <NavLink key="conversations" to="/conversations"
                  className={({ isActive }) => `flex items-center gap-3 h-12 px-3 rounded-[10px] font-semibold text-[14px] ${isActive ? 'bg-sideon text-white' : 'text-indigo-200 active:bg-white/10'}`}>
                  <MessagesSquare size={20} strokeWidth={1.8} /><span className="flex-1">Conversations</span>
                </NavLink>
              )] : [row(s)]))}
              {me.spaces.includes('settings') && <div className="mt-2 pt-2 border-t border-white/10">{row('settings')}</div>}
              <div className="mt-2 pt-2 border-t border-white/10 flex flex-col">
                <NavLink to="/notifications" className="flex items-center h-11 px-3 rounded-[10px] text-indigo-200 text-[13.5px] font-semibold active:bg-white/10">Notifications and settings</NavLink>
                {me.staff.role !== 'consultant' && <NavLink to="/rota" className="flex items-center h-11 px-3 rounded-[10px] text-indigo-200 text-[13.5px] font-semibold active:bg-white/10">Hours, rota and my leave</NavLink>}
                <NavLink to="/account" className="flex items-center gap-2 h-11 px-3 rounded-[10px] text-indigo-200 text-[13.5px] font-semibold active:bg-white/10"><ShieldCheck size={16} /> Sign-in and security</NavLink>
                {me.staff.role === 'admin' && me.lifecycle && me.lifecycle.status !== 'active' && <NavLink to="/onboarding" className="flex items-center gap-2 h-11 px-3 rounded-[10px] text-indigo-200 text-[13.5px] font-semibold active:bg-white/10"><Rocket size={16} /> Getting started</NavLink>}
              </div>
            </div>
            <div className="shrink-0 border-t border-white/10 p-3 flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-indigo-200 text-sideon font-bold text-[12px] flex items-center justify-center shrink-0">{initials(me.staff.name)}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[13px] truncate">{me.staff.name}</div>
                <div className="text-indigo-300 text-[11.5px] truncate">{ROLE_LABELS[me.staff.role]}</div>
              </div>
              {!MOCK && <button className="h-11 px-3 rounded-lg bg-white/10 font-semibold text-[13px] inline-flex items-center gap-1.5" onClick={() => { setOpen(false); void disablePush().catch(() => {}).finally(() => signOut()) }}><LogOut size={15} /> Sign out</button>}
            </div>
          </nav>
        </div>
      )}
    </>
  )
}
